import React, { useState, useCallback, useEffect } from "react";
import apiService from '../services/api';

const INITIAL_DATA = {
    invoiceNo: "",
    dated: "",
    dueDate: "",
    vendor: {
        name: "",
        address: "",
        gstin: "",
        pan: "",
    },
    billTo: {
        name: "",
        address: "",
        gstin: "",
        pan: "",
        email: "",
        udyam: "",
        state: "",
    },
    lineItems: [],
    bank: {
        name: "",
        accountNo: "",
        branch: "",
        ifsc: "",
    },
    totals: {
        taxableValue: "",
        cgst: "",
        sgst: "",
        taxAmount: "",
        invoiceTotal: "",
    },
    notes: [],
    remarks: "",
};

const EMPTY_LINE_ITEM = { particulars: "", tripId: "", hsnSac: "", rate: "", taxable: "", cgst: "", sgst: "", total: "" };

function EditableField({ value, onChange, multiline = false, mono = false, placeholder = "—" }: any) {
    const [focused, setFocused] = useState(false);
    const baseStyle: React.CSSProperties = {
        fontFamily: mono ? "'DM Mono', monospace" : "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        color: "inherit",
        background: focused ? "#f0fdf4" : "transparent",
        border: focused ? "1.5px solid #10b981" : "1.5px solid transparent",
        borderRadius: 6,
        padding: focused ? "3px 7px" : "3px 4px",
        width: "100%",
        resize: "none",
        outline: "none",
        lineHeight: 1.5,
        cursor: "text",
        transition: "all 0.15s ease",
        boxSizing: "border-box",
        display: "block",
    };
    if (multiline) {
        return (
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={placeholder}
                rows={2}
                style={baseStyle}
            />
        );
    }
    return (
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            style={baseStyle}
        />
    );
}

function Section({ title, icon, children, action }: any) {
    return (
        <div style={{ background: "white", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>{icon}</span>
                    <h3 style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>{title}</h3>
                </div>
                {action}
            </div>
            <div style={{ padding: 20 }}>{children}</div>
        </div>
    );
}

function FieldRow({ label, value, onChange, multiline, mono }: any) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
            <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>
                <EditableField value={value} onChange={onChange} multiline={multiline} mono={mono} placeholder={label} />
            </div>
        </div>
    );
}

function IconBtn({ onClick, title, children }: any) {
    const [hov, setHov] = useState(false);
    return (
        <button
            onClick={onClick}
            title={title}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 5px", borderRadius: 4, color: hov ? "#ef4444" : "#cbd5e1", fontSize: 13, lineHeight: 1, transition: "color 0.15s", fontFamily: "inherit" }}
        >
            {children}
        </button>
    );
}

/** Format currency like backend PDF */
function formatCurrency(amount: string | number | undefined) {
    if (amount === undefined || amount === null || amount === "") return "—";
    const num = Number(String(amount).replace(/[^0-9.-]/g, ""));
    if (isNaN(num)) return String(amount);
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Live invoice preview – same format as backend createVendorInvoicePDF.js */
function InvoiceLivePreview({ data }: { data: any }) {
    const border = "1px solid #e5e7eb";
    const borderB = { borderBottom: "1px solid #e5e7eb" };
    const bgGray = { background: "#f3f4f6" };
    const textXs = { fontSize: 10, color: "#6b7280" };
    const textSm = { fontSize: 12 };
    const fontBold = { fontWeight: 700 };
    const invoiceLabel = { background: "#f3f4f6", color: "#111827", fontWeight: 700, padding: "10px 12px", width: "40%" };
    const invoiceValue = { padding: "10px 12px", color: "#0f172a", fontWeight: 700, fontSize: 12 };
    const invoiceValueSmall = { padding: "10px 12px", color: "#0f172a", fontWeight: 600, fontSize: 11 };

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto", background: "#fff", border, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", fontFamily: "Arial, sans-serif", fontSize: 12, color: "#1f2937" }}>
            {/* Header: Vendor (biller) details */}
            <div style={{ padding: "16px 20px", ...borderB }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{data.vendor?.name || "—"}</div>
                <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{data.vendor?.address || "—"}</div>
                <div style={textXs}>GSTIN/UIN: {data.vendor?.gstin || "—"}</div>
                <div style={textXs}>PAN: {data.vendor?.pan || "—"}</div>
            </div>

            {/* Invoice Details + Bill To (same layout as backend) */}
            <div style={{ border, ...borderB, borderBottomWidth: 0 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr" }}>
                    <div style={{ borderRight: "1px solid #e5e7eb" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <tbody>
                                <tr><td style={invoiceLabel}>Invoice No</td><td style={invoiceValueSmall}>{data.invoiceNo || "N/A"}</td></tr>
                                <tr><td style={invoiceLabel}>Dated</td><td style={invoiceValue}>{data.dated || "N/A"}</td></tr>
                                <tr><td style={invoiceLabel}>Due Date</td><td style={invoiceValue}>{data.dueDate || "N/A"}</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <div style={{ ...bgGray, padding: "12px", ...fontBold, ...borderB }}>Bill To</div>
                        <div style={{ padding: 12 }}>
                            <div style={{ ...textSm, ...fontBold }}>{data.billTo?.name || "—"}</div>
                            <div style={{ ...textXs, whiteSpace: "pre-wrap" }}>{data.billTo?.address || "—"}</div>
                            <div style={textXs}>GSTIN/UIN: {data.billTo?.gstin || "—"}</div>
                            {data.billTo?.state && <div style={textXs}>State: {data.billTo.state}</div>}
                            {data.billTo?.email && <div style={textXs}>Email: {data.billTo.email}</div>}
                            {data.billTo?.udyam && <div style={textXs}>{data.billTo.udyam}</div>}
                            <div style={textXs}>PAN: {data.billTo?.pan || "—"}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Items Table (same columns as backend) */}
            <div style={{ padding: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={bgGray}>
                            <th style={{ padding: 10, border, ...fontBold }}>Particulars</th>
                            <th style={{ padding: 10, border, ...fontBold }}>Trip ID</th>
                            <th style={{ padding: 10, border, ...fontBold }}>HSN/SAC</th>
                            <th style={{ padding: 10, border, ...fontBold }}>Rate</th>
                            <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>Taxable</th>
                            <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>CGST</th>
                            <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>SGST</th>
                            <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.lineItems && data.lineItems.length > 0) ? data.lineItems.map((item: any, i: number) => (
                            <tr key={i}>
                                <td style={{ padding: 10, border, verticalAlign: "top" }}>
                                    <div>{item.particulars || "Logistics Services"}</div>
                                    {item.tripId && <div style={textXs}>Trip ID: {item.tripId}</div>}
                                </td>
                                <td style={{ padding: 10, border }}>{item.tripId || "—"}</td>
                                <td style={{ padding: 10, border }}>{item.hsnSac || "996511"}</td>
                                <td style={{ padding: 10, border }}>{item.rate ? formatCurrency(item.rate) : "—"}</td>
                                <td style={{ padding: 10, border, textAlign: "right" }}>{item.taxable ? formatCurrency(item.taxable) : "—"}</td>
                                <td style={{ padding: 10, border, textAlign: "right" }}>{item.cgst ? formatCurrency(item.cgst) : "—"}</td>
                                <td style={{ padding: 10, border, textAlign: "right" }}>{item.sgst ? formatCurrency(item.sgst) : "—"}</td>
                                <td style={{ padding: 10, border, textAlign: "right" }}>{item.total ? formatCurrency(item.total) : "—"}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={8} style={{ padding: 12, border, textAlign: "center", color: "#6b7280" }}>No line items</td></tr>
                        )}
                        <tr style={bgGray}>
                            <td colSpan={4} style={{ padding: 10, border, textAlign: "right", ...fontBold }}>Totals</td>
                            <td style={{ padding: 10, border, textAlign: "right", ...fontBold }}>{formatCurrency(data.totals?.taxableValue)}</td>
                            <td style={{ padding: 10, border, textAlign: "right", ...fontBold }}>{formatCurrency(data.totals?.cgst)}</td>
                            <td style={{ padding: 10, border, textAlign: "right", ...fontBold }}>{formatCurrency(data.totals?.sgst)}</td>
                            <td style={{ padding: 10, border, textAlign: "right", ...fontBold }}>{formatCurrency(data.totals?.invoiceTotal)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Totals & Bank Details (same 2-col as backend) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", ...borderB }}>
                <div style={{ borderRight: "1px solid #e5e7eb", padding: 16 }}>
                    <div style={{ ...fontBold, marginBottom: 8 }}>Company Bank Details</div>
                    <div style={textSm}>Bank Name: {data.bank?.name || "—"}</div>
                    <div style={textSm}>A/C No.: {data.bank?.accountNo || "—"}</div>
                    <div style={textSm}>Branch: {data.bank?.branch || "—"}</div>
                    <div style={textSm}>IFSC Code: {data.bank?.ifsc || "—"}</div>
                    {data.notes?.filter((n: string) => n?.trim()).length > 0 && (
                        <>
                            <div style={{ ...fontBold, marginTop: 12, marginBottom: 4 }}>Notes :</div>
                            <div style={textXs}>{data.notes.filter((n: string) => n?.trim()).join("; ")}</div>
                        </>
                    )}
                </div>
                <div style={{ padding: 16 }}>
                    <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>Total Taxable Value</span><span>{formatCurrency(data.totals?.taxableValue)}</span></div>
                    {(data.totals?.cgst || data.totals?.cgst === 0) && <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>CGST</span><span>{formatCurrency(data.totals?.cgst)}</span></div>}
                    {(data.totals?.sgst || data.totals?.sgst === 0) && <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>SGST</span><span>{formatCurrency(data.totals?.sgst)}</span></div>}
                    <div style={{ ...textSm, display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e5e7eb", paddingBottom: 8, marginTop: 8 }}><span>Tax Amount</span><span>{formatCurrency(data.totals?.taxAmount)}</span></div>
                    <div style={{ fontSize: 16, ...fontBold, display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span>Invoice Total</span>
                        <span style={{ color: "#15803d" }}>{formatCurrency(data.totals?.invoiceTotal)}</span>
                    </div>
                </div>
            </div>

            {/* Notes & Remarks (same as backend) */}
            <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, borderTop: "1px solid #e5e7eb" }}>
                <div>
                    <div style={{ ...fontBold, marginBottom: 8 }}>Notes :</div>
                    <ul style={{ margin: 0, paddingLeft: 18, ...textXs }}>
                        <li>Payment terms: As per agreed credit period.</li>
                        <li>All disputes will fall under the jurisdiction of Mumbai courts.</li>
                        <li>Please ensure payment is made within the due date to avoid any delays.</li>
                    </ul>
                </div>
                <div>
                    <div style={{ ...fontBold, marginBottom: 8 }}>Remarks :</div>
                    <div style={textXs}>{data.remarks?.trim() || "Being GST in this invoice is to be paid by vendor."}</div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 16px", textAlign: "center", ...textXs, background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
                Subject to Mumbai jurisdiction, This is a Computer Generated Invoice
            </div>
        </div>
    );
}

function TCell({ value, onChange, mono, style = {} }: any) {
    return (
        <td style={{ padding: "5px 8px 5px 0", verticalAlign: "top", ...style }}>
            <div style={{ fontSize: 12, color: "#475569", fontFamily: mono ? "'DM Mono', monospace" : "inherit" }}>
                <EditableField value={value} onChange={onChange} mono={mono} multiline={!mono && (value?.length ?? 0) > 40} />
            </div>
        </td>
    );
}

const Invoice: React.FC = () => {
    const [data, setData] = useState<any>(INITIAL_DATA);
    const [savedData, setSavedData] = useState<any>(INITIAL_DATA);
    const [isDirty, setIsDirty] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [dbRecordId, setDbRecordId] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [saved, setSaved] = useState(false);
    const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
    const [uploadedS3Url, setUploadedS3Url] = useState<string | null>(null);

    // Revoke object URL on unmount or when PDF changes
    useEffect(() => {
        return () => {
            if (uploadedPdfUrl) URL.revokeObjectURL(uploadedPdfUrl);
        };
    }, [uploadedPdfUrl]);

    const update = (path: string, value: any) => {
        setData((prev: any) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
            obj[keys[keys.length - 1]] = value;
            return next;
        });
        setIsDirty(true);
    };

    const updateLineItem = (idx: number, field: string, value: any) => {
        setData((prev: any) => ({ ...prev, lineItems: prev.lineItems.map((it: any, i: number) => i === idx ? { ...it, [field]: value } : it) }));
        setIsDirty(true);
    };

    const addLineItem = () => { setData((prev: any) => ({ ...prev, lineItems: [...prev.lineItems, { ...EMPTY_LINE_ITEM }] })); setIsDirty(true); };
    const removeLineItem = (idx: number) => { setData((prev: any) => ({ ...prev, lineItems: prev.lineItems.filter((_: any, i: number) => i !== idx) })); setIsDirty(true); };

    const updateNote = (idx: number, value: any) => {
        setData((prev: any) => ({ ...prev, notes: prev.notes.map((n: any, i: number) => i === idx ? value : n) }));
        setIsDirty(true);
    };
    const addNote = () => { setData((prev: any) => ({ ...prev, notes: [...prev.notes, ""] })); setIsDirty(true); };
    const removeNote = (idx: number) => { setData((prev: any) => ({ ...prev, notes: prev.notes.filter((_: any, i: number) => i !== idx) })); setIsDirty(true); };

    const handleSave = async () => {
        setLoading(true);
        try {
            if (dbRecordId) {
                // Update existing record
                const res = await fetch(`https://vendor-invoice.vercel.app/api/invoices/${dbRecordId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...data, fileName, fileUrl: uploadedS3Url }),
                });
                const result = await res.json();
                if (!result.success) throw new Error(result.error);
            } else {
                // Create new record
                const res = await fetch("https://vendor-invoice.vercel.app/api/invoices", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...data, fileName, fileUrl: uploadedS3Url }),
                });
                const result = await res.json();
                if (!result.success) throw new Error(result.error);
                setDbRecordId(result.id);
            }
            setSavedData(data);
            setIsDirty(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err: any) {
            setError("Failed to save to database: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => { setData(savedData); setIsDirty(false); };

    const extractFromPDF = useCallback(async (file: File) => {
        setLoading(true);
        setError(null);
        try {
            const base64: string = await new Promise((res, rej) => {
                const reader = new FileReader();
                reader.onload = () => res((reader.result as string).split(",")[1]);
                reader.onerror = () => rej(new Error("Failed to read file"));
                reader.readAsDataURL(file);
            });

            const result = await apiService.extractInvoice(base64, file.name, file.type);
            const { fileUrl, ...parsed } = result as any;

            if (fileUrl) {
                setUploadedS3Url(fileUrl);
            }

            // Auto-save to MongoDB
            const dbRecordId = await apiService.saveInvoice({
                ...parsed,
                fileName: file.name,
                fileUrl: fileUrl || undefined,
            });
            setDbRecordId(dbRecordId);

            setData(parsed);
            setSavedData(parsed);
            setFileName(file.name);
            setIsDirty(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err: any) {
            setError("Failed to extract invoice data: " + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleFile = (file: File) => {
        if (file?.type !== "application/pdf") {
            setError("Please upload a valid PDF file.");
            return;
        }
        if (uploadedPdfUrl) URL.revokeObjectURL(uploadedPdfUrl);
        setUploadedPdfUrl(URL.createObjectURL(file));
        extractFromPDF(file);
    };

    return (
        <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#f8fafc", minHeight: "100vh", padding: "20px" }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, textarea { font-family: inherit; }
        input::placeholder, textarea::placeholder { color: #cbd5e1; font-style: italic; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.35s ease forwards; }
        .row-actions { opacity: 0; transition: opacity 0.15s; }
        tr:hover .row-actions, .note-row:hover .row-actions { opacity: 1; }
        input:hover, textarea:hover { background: #f8fafc !important; border-color: #e2e8f0 !important; }
        @media (max-width: 1024px) {
          .invoice-with-preview { grid-template-columns: 1fr !important; }
          .invoice-with-preview .pdf-sticky { position: static !important; }
        }
      `}</style>

            {/* Upload Zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => {
                    const input = document.getElementById("pdf-upload");
                    if (input) input.click();
                }}
                style={{
                    border: `2px dashed ${dragOver ? "#10b981" : "#cbd5e1"}`,
                    borderRadius: 14, padding: "20px", textAlign: "center",
                    background: dragOver ? "#f0fdf4" : "white", cursor: "pointer",
                    marginBottom: 16, transition: "all 0.2s ease",
                }}
            >
                <input id="pdf-upload" type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
                {loading ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                        <div style={{ width: 20, height: 20, border: "2.5px solid #e2e8f0", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Extracting invoice fields with AI...</span>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                        <span style={{ fontSize: 22 }}>📤</span>
                        <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Upload a new PDF invoice</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>Drag & drop or click · All fields auto-extracted and fully editable</div>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: "#ef4444", fontSize: 12 }}>
                    ⚠️ {error}
                </div>
            )}

            {/* Edit tip */}
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "7px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>✏️</span>
                <span style={{ fontSize: 12, color: "#92400e", fontWeight: 500 }}>All fields are editable — click any value to modify it. Add or remove rows and notes freely.</span>
            </div>

            <div className="invoice-with-preview" style={{ display: "grid", gridTemplateColumns: "1fr minmax(320px, 400px)", gap: 20, alignItems: "start" }}>
            <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Row 1 */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    <Section title="Invoice Details" icon="🧾">
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <FieldRow label="Invoice No" value={data.invoiceNo} onChange={(v: any) => update("invoiceNo", v)} />
                            <FieldRow label="Date" value={data.dated} onChange={(v: any) => update("dated", v)} />
                            <FieldRow label="Due Date" value={data.dueDate} onChange={(v: any) => update("dueDate", v)} />
                        </div>
                    </Section>

                    <Section title="Vendor" icon="🏢">
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <FieldRow label="Name" value={data.vendor.name} onChange={(v: any) => update("vendor.name", v)} />
                            <FieldRow label="Address" value={data.vendor.address} onChange={(v: any) => update("vendor.address", v)} multiline />
                            <FieldRow label="GSTIN" value={data.vendor.gstin} onChange={(v: any) => update("vendor.gstin", v)} mono />
                            <FieldRow label="PAN" value={data.vendor.pan} onChange={(v: any) => update("vendor.pan", v)} mono />
                        </div>
                    </Section>

                    <Section title="Bill To" icon="📬">
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <FieldRow label="Company" value={data.billTo.name} onChange={(v: any) => update("billTo.name", v)} />
                            <FieldRow label="Address" value={data.billTo.address} onChange={(v: any) => update("billTo.address", v)} multiline />
                            <FieldRow label="GSTIN" value={data.billTo.gstin} onChange={(v: any) => update("billTo.gstin", v)} mono />
                            <FieldRow label="PAN" value={data.billTo.pan} onChange={(v: any) => update("billTo.pan", v)} mono />
                            <FieldRow label="Email" value={data.billTo.email} onChange={(v: any) => update("billTo.email", v)} />
                            <FieldRow label="UDYAM" value={data.billTo.udyam} onChange={(v: any) => update("billTo.udyam", v)} mono />
                            <FieldRow label="State" value={data.billTo.state} onChange={(v: any) => update("billTo.state", v)} />
                        </div>
                    </Section>
                </div>

                {/* Line Items */}
                <Section
                    title="Line Items"
                    icon="📋"
                    action={
                        <button onClick={addLineItem} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "4px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                            + Add Row
                        </button>
                    }
                >
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "1.5px solid #f1f5f9" }}>
                                    {["Particulars", "Trip ID", "HSN/SAC", "Rate", "Taxable", "CGST", "SGST", "Total", ""].map((h) => (
                                        <th key={h} style={{ paddingBottom: 10, paddingRight: 8, textAlign: "left", fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.lineItems.map((item: any, i: number) => (
                                    <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                                        <TCell value={item.particulars} onChange={(v: any) => updateLineItem(i, "particulars", v)} style={{ minWidth: 180, maxWidth: 240 }} />
                                        <TCell value={item.tripId} onChange={(v: any) => updateLineItem(i, "tripId", v)} style={{ minWidth: 70 }} />
                                        <TCell value={item.hsnSac} onChange={(v: any) => updateLineItem(i, "hsnSac", v)} mono style={{ minWidth: 80 }} />
                                        <TCell value={item.rate} onChange={(v: any) => updateLineItem(i, "rate", v)} mono style={{ minWidth: 90 }} />
                                        <TCell value={item.taxable} onChange={(v: any) => updateLineItem(i, "taxable", v)} mono style={{ minWidth: 90 }} />
                                        <TCell value={item.cgst} onChange={(v: any) => updateLineItem(i, "cgst", v)} mono style={{ minWidth: 80 }} />
                                        <TCell value={item.sgst} onChange={(v: any) => updateLineItem(i, "sgst", v)} mono style={{ minWidth: 80 }} />
                                        <TCell value={item.total} onChange={(v: any) => updateLineItem(i, "total", v)} mono style={{ minWidth: 90 }} />
                                        <td style={{ padding: "5px 0", verticalAlign: "top" }}>
                                            <span className="row-actions">
                                                <IconBtn onClick={() => removeLineItem(i)} title="Remove row">🗑</IconBtn>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* Row 3 */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    <Section title="Bank Details" icon="🏦">
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            <FieldRow label="Bank Name" value={data.bank.name} onChange={(v: any) => update("bank.name", v)} />
                            <FieldRow label="Account No." value={data.bank.accountNo} onChange={(v: any) => update("bank.accountNo", v)} mono />
                            <FieldRow label="Branch" value={data.bank.branch} onChange={(v: any) => update("bank.branch", v)} />
                            <FieldRow label="IFSC" value={data.bank.ifsc} onChange={(v: any) => update("bank.ifsc", v)} mono />
                        </div>
                    </Section>

                    <Section title="Tax Summary" icon="📊">
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {[["Taxable Value", "totals.taxableValue"], ["CGST (9%)", "totals.cgst"], ["SGST (9%)", "totals.sgst"], ["Tax Amount", "totals.taxAmount"]].map(([label, path]) => (
                                <div key={path} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                    <span style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>{label}</span>
                                    <div style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: "#334155", maxWidth: 130 }}>
                                        <EditableField value={path.split(".").reduce((o, k) => o?.[k], data)} onChange={(v: any) => update(path, v)} mono />
                                    </div>
                                </div>
                            ))}
                            <div style={{ borderTop: "1.5px solid #e2e8f0", paddingTop: 10, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Invoice Total</span>
                                <div style={{ fontSize: 15, fontFamily: "'DM Mono', monospace", fontWeight: 700, color: "#10b981", maxWidth: 140 }}>
                                    <EditableField value={data.totals.invoiceTotal} onChange={(v: any) => update("totals.invoiceTotal", v)} mono />
                                </div>
                            </div>
                        </div>
                    </Section>

                    <Section
                        title="Notes & Remarks"
                        icon="📝"
                        action={
                            <button onClick={addNote} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Note</button>
                        }
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {data.notes.map((note: any, i: number) => (
                                <div key={i} className="note-row" style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                                    <span style={{ color: "#cbd5e1", marginTop: 5, flexShrink: 0, fontSize: 12 }}>•</span>
                                    <div style={{ flex: 1, fontSize: 12, color: "#475569" }}>
                                        <EditableField value={note} onChange={(v: any) => updateNote(i, v)} multiline placeholder="Note text..." />
                                    </div>
                                    <span className="row-actions" style={{ flexShrink: 0, marginTop: 3 }}>
                                        <IconBtn onClick={() => removeNote(i)} title="Remove note">✕</IconBtn>
                                    </span>
                                </div>
                            ))}
                            <div style={{ marginTop: 8, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
                                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Remarks</div>
                                <div style={{ fontSize: 12, color: "#475569" }}>
                                    <EditableField value={data.remarks} onChange={(v: any) => update("remarks", v)} placeholder="Remarks..." />
                                </div>
                            </div>
                        </div>
                    </Section>
                </div>

                {/* Footer bar */}
                <div style={{ background: "#0f172a", borderRadius: 14, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", gap: 28 }}>
                        {[
                            { label: "Invoice", value: data.invoiceNo, mono: true, color: "white" },
                            { label: "Due", value: data.dueDate, mono: false, color: "#fbbf24" },
                            { label: "Line Items", value: data.lineItems.length, mono: false, color: "white" },
                        ].map(({ label, value, mono, color }) => (
                            <div key={label}>
                                <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                                <div style={{ fontSize: 14, color, fontWeight: 700, fontFamily: mono ? "'DM Mono', monospace" : "inherit" }}>{value}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        {isDirty && (
                            <>
                                <button onClick={handleReset} style={{ background: "#1e293b", border: "1px solid #334155", color: "#94a3b8", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>↩ Discard</button>
                                <button onClick={handleSave} style={{ background: "#10b981", border: "none", color: "white", padding: "7px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>💾 Save</button>
                            </>
                        )}
                        {saved && !isDirty && <div style={{ color: "#6ee7b7", fontSize: 12, fontWeight: 600 }}>✓ Saved</div>}
                        <div>
                            <div style={{ fontSize: 11, color: "#64748b", textAlign: "right" }}>Total Amount</div>
                            <div style={{ fontSize: 22, color: "#10b981", fontWeight: 800, fontFamily: "'DM Mono', monospace" }}>{data.totals.invoiceTotal}</div>
                        </div>
                    </div>
                </div>

            </div>

                {/* Invoice View - right column: live preview from current data (updates on every edit) */}
                <div className="pdf-sticky" style={{ position: "sticky", top: 20 }}>
                <Section title="Invoice View" icon="📄">
                    <div style={{ minHeight: 420 }}>
                        <p style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8, fontWeight: 600 }}>LIVE PREVIEW · Updates as you edit</p>
                        <div style={{ maxHeight: 520, overflowY: "auto" }}>
                            <InvoiceLivePreview data={data} />
                        </div>
                        {uploadedPdfUrl && (
                            <a
                                href={uploadedPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 11, color: "#0ea5e9", fontWeight: 600, textDecoration: "none" }}
                            >
                                📎 Open original PDF {fileName ? `(${fileName})` : ""}
                            </a>
                        )}
                        {uploadedS3Url && (
                            <a
                                href={uploadedS3Url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, marginLeft: 12, fontSize: 11, color: "#15803d", fontWeight: 600, textDecoration: "none" }}
                            >
                                ☁ Open S3 PDF
                            </a>
                        )}
                    </div>
                </Section>
                </div>
            </div>
        </div>
    );
};

export default Invoice;
